import {Material, Object3D, Vector3, Mesh, BufferGeometry} from "three";
import {Building} from "../Building";
import {IBuildingMetrics} from "../../config/Config";
import {Main} from "../../Main";
import {WallAddon} from "../../scene/addon/WallAddon";
import {THREEUtils} from "../../utils/THREEUtils";
import {ISceneAddon} from "../../scene/addon/ISceneAddon";
import {Wall} from "./Wall";
import {EndWall, EndWallConfig} from "./EndWall";
import {WallConfig} from "./Wall";

interface IWallContainerXConfig
{
	material: Material;
	sideGeometry: BufferGeometry;
	endGeometryFront: BufferGeometry;
	endGeometryBack: BufferGeometry;
	endRoofGeometry?: BufferGeometry;
	setRotations?: boolean;
}

export interface IWall
{
	id: string;
	normal: Vector3;
	isSideWall: boolean;
	original: Mesh;
	current: Mesh;
	roof?: Mesh;
	roofOriginal?: Mesh;
}

export class WallContainer
{

	public left: Wall;
	public right: Wall;
	public front: EndWall;
	public back: EndWall;

	public scene: Object3D;

	public sideGeometry: BufferGeometry;
	public endGeometryFront: BufferGeometry;
	public endGeometryBack: BufferGeometry;
	public endRoofGeometry: BufferGeometry;

	public material: Material;

	constructor(config: IWallContainerXConfig)
	{
		this.sideGeometry = config.sideGeometry;
		this.endGeometryFront = config.endGeometryFront;
		this.endGeometryBack = config.endGeometryBack;
		this.endRoofGeometry = config.endRoofGeometry;
		this.material = config.material;

		const setRotations = config.setRotations !== undefined ? config.setRotations : true;
		this.init(setRotations);
	}

	// ======================================================================================================
	// Init

	private init(setRotations: boolean)
	{
		const left = new Mesh(this.sideGeometry, this.material);
		const right = new Mesh(this.sideGeometry, this.material);
		const front = new Mesh(this.endGeometryFront, this.material);
		const back = new Mesh(this.endGeometryBack, this.material);
		left.name = "wallLeft";
		right.name = "wallRight";
		front.name = "wallFront";
		back.name = "wallBack";

		if (setRotations)
		{
			const PI_HALF = Math.PI / 2;
			left.rotation.y = -PI_HALF;
			right.rotation.y = PI_HALF;
			back.rotation.y = Math.PI;
		}

		this.left = new Wall(this.createWallConfig(Building.WALLS.LEFT, left));
		this.right = new Wall(this.createWallConfig(Building.WALLS.RIGHT, right));
		this.front = new EndWall(this.createEndWallConfig(Building.WALLS.FRONT, front));
		this.back = new EndWall(this.createEndWallConfig(Building.WALLS.BACK, back));

		if (this.endRoofGeometry)
		{
			this.back.roof.rotation.y = Math.PI;
		}
	}

	private createWallConfig(id: string, mesh: Mesh, isSideWall: boolean = true): WallConfig
	{
		const normal: Vector3 = Building.NORMALS[id];

		return {
			id: id,
			original: mesh,
			current: mesh,
			normal: normal,
			isSideWall: isSideWall
		};
	}

	private createEndWallConfig(id: string, mesh: Mesh): EndWallConfig
	{
		let roof = null;
		let roofOriginal = null;
		if (this.endRoofGeometry)
		{
			roof = new Mesh(this.endRoofGeometry, this.material);
			roofOriginal = new Mesh(this.endRoofGeometry, this.material);
		}

		const normal: Vector3 = Building.NORMALS[id];
		return {
			id: id,
			original: mesh,
			current: mesh,
			normal: normal,
			isSideWall: false,
			roof: roof,
			roofOriginal: roofOriginal
		};
	}

	// ======================================================================================================
	// Add, remove

	public add(scene?: Object3D)
	{
		scene = scene || this.scene;
		this.scene = scene;

		scene.add(this.left.current);
		scene.add(this.right.current);
		scene.add(this.front.current);
		scene.add(this.back.current);

		if (this.endRoofGeometry)
		{
			scene.add(this.front.roof);
			scene.add(this.back.roof);
		}
	}

	public remove()
	{
		const scene = this.scene;
		scene.remove(this.left.current);
		scene.remove(this.right.current);
		scene.remove(this.front.current);
		scene.remove(this.back.current);

		if (this.endRoofGeometry)
		{
			scene.remove(this.front.roof);
			scene.remove(this.back.roof);
		}
	}

	// ======================================================================================================
	// Update

	public update(metrics: IBuildingMetrics, top: number, bottom: number, wainScot: boolean = false)
	{
		const width = metrics.width;
		const width_half = metrics.width_half;
		const height = (top - bottom);
		const pos_y = bottom + height / 2;
		const length = metrics.length;
		const length_half = metrics.length_half;
		const roof_height = metrics.roof_height;

		const wall_left = this.left.original;
		const wall_right = this.right.original;
		const wall_front = this.front.original;
		const wall_back = this.back.original;

		wall_left.position.x = -width_half;
		wall_left.position.y = pos_y;
		wall_left.scale.x = length;
		wall_left.scale.y = height;

		let singleSlopeHeight = 0;
		const building = Main.getInstance().config.building;

		wall_front.position.z = length_half;
		wall_front.position.y = pos_y;
		wall_front.scale.x = width;
		wall_front.scale.y = height;

		wall_back.position.z = -length_half;
		wall_back.position.y = pos_y;
		wall_back.scale.x = width;
		wall_back.scale.y = height;

		if (building.single_slope.value && wainScot === false)
		{
			singleSlopeHeight = building.width.value * building.pitch.value / 12;

			const fullHeight = height + singleSlopeHeight;

			this.front.hideRoof();
			this.back.hideRoof();
			const smallerY = 0.5 * ((height - singleSlopeHeight) / (fullHeight));
			const largerY = 0.5;

			wall_front.geometry.getAttribute("position").setY(0, smallerY).needsUpdate = true;
			wall_front.geometry.getAttribute("position").setY(1, largerY).needsUpdate = true;
			wall_front.geometry.getAttribute("uv").setY(1, fullHeight).needsUpdate = true;

			wall_back.geometry.getAttribute("position").setY(0, largerY).needsUpdate = true;
			wall_back.geometry.getAttribute("position").setY(1, smallerY).needsUpdate = true;
			wall_back.geometry.getAttribute("uv").setY(0, fullHeight).needsUpdate = true;

			wall_front.scale.y = fullHeight;
			wall_front.position.y = pos_y + singleSlopeHeight / 2;
			wall_back.scale.y = fullHeight;
			wall_back.position.y = pos_y + singleSlopeHeight / 2;
		}
		else
		{
			if (this.front.roof)
			{
				this.front.showRoof();
			}
			if (this.back.roof)
			{
				this.back.showRoof();
			}

			const y = 0.5;

			wall_front.geometry.getAttribute("position").setY(0, y).needsUpdate = true;
			wall_front.geometry.getAttribute("position").setY(1, y).needsUpdate = true;
			wall_back.geometry.getAttribute("position").setY(0, y).needsUpdate = true;
			wall_back.geometry.getAttribute("position").setY(1, y).needsUpdate = true;
		}

		wall_right.position.x = width_half;
		wall_right.position.y = pos_y + singleSlopeHeight / 2;
		wall_right.scale.x = length;
		wall_right.scale.y = height + singleSlopeHeight;

		// roof

		if (!this.endRoofGeometry)
		{
			return;
		}

		const roof_front = this.front.roofOriginal;
		const roof_back = this.back.roofOriginal;

		roof_front.position.z = length / 2;
		roof_front.position.y = metrics.height;
		roof_front.scale.x = width;
		roof_front.scale.y = roof_height;

		roof_back.position.z = -length / 2;
		roof_back.position.y = metrics.height;
		roof_back.scale.x = width;
		roof_back.scale.y = roof_height;
	}

	// ======================================================================================================
	// Addons, cuts

	/**
	 * Clean is needed for walls for some reason (bad geometry?)
	 * but not for cubes.
	 */
	public addNode(node: WallAddon, clean: boolean = true)
	{
		const wallId = node.parentId;
		const wall: IWall = this[wallId];

		const wall_mesh = wall.current;
		const subtractNodes = [node];

		let cleanNormal = null;
		if (clean)
		{
			cleanNormal = wall.normal;
		}
		const new_wall = THREEUtils.subtractNodesFromMeshWithClean(wall_mesh, <any>subtractNodes, cleanNormal);

		// todo dispose of unneeded old geometry ?
		// problem: it might still be used by other walls
		// -> leak issue !
		// wall_mesh.geometry.dispose();

		this.scene.remove(wall_mesh);
		this.scene.add(new_wall);

		this.setNewCurrent(wallId, new_wall);

		if (wall.roof)
		{
			const new_roof = THREEUtils.subtractNodesFromMeshWithClean(wall.roofOriginal, <any>subtractNodes, cleanNormal);
			this.scene.remove(wall.roof);
			this.scene.add(new_roof);
			new_roof.visible = wall.roof.visible;
			wall.roof = new_roof;
		}
	}

	public updateCuts(nodes: {[wallId: string]: ISceneAddon[]}, clean: boolean = true)
	{
		for (const key in Building.WALLS)
		{
			const wallId = Building.WALLS[key];
			const wall: Wall = this[wallId];
			this.updateCutsForWall(wall, nodes, clean);
		}
		this.updateCutsForWall(this.front, nodes, clean);
		this.updateCutsForWall(this.back, nodes, clean);
	}

	private updateCutsForWall(wall: IWall, nodes: {[wallId: string]: ISceneAddon[]}, clean: boolean = true)
	{
		const wallNodes = nodes[wall.id];

		let cleanNormal = null;
		if (clean)
		{
			cleanNormal = wall.normal;
		}
		const new_wall = THREEUtils.subtractNodesFromMeshWithClean(wall.original, wallNodes, cleanNormal);

		this.scene.remove(wall.current);
		this.scene.add(new_wall);
		new_wall.visible = wall.current.visible;
		wall.current = new_wall;

		if (wall.roof)
		{
			const new_roof = THREEUtils.subtractNodesFromMeshWithClean(wall.roofOriginal, wallNodes, cleanNormal);
			this.scene.remove(wall.roof);
			this.scene.add(new_roof);
			new_roof.visible = wall.roof.visible;
			wall.roof = new_roof;
		}
	}

	// ======================================================================================================
	// Show, hide

	public show()
	{
		this.left.current.visible = true;
		this.right.current.visible = true;
		this.front.current.visible = true;
		this.back.current.visible = true;

		if (this.endRoofGeometry)
		{
			this.front.roof.visible = true;
			this.back.roof.visible = true;
		}
	}

	public hide()
	{
		this.left.current.visible = false;
		this.right.current.visible = false;
		this.front.current.visible = false;
		this.back.current.visible = false;

		if (this.endRoofGeometry)
		{
			this.front.roof.visible = false;
			this.back.roof.visible = false;
		}
	}

	public setNewCurrent(wallId: string, new_wall: Mesh)
	{
		new_wall.visible = this[wallId].current.visible;
		this[wallId].current = new_wall;
	}
}